import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { login as apiLogin, logout as apiLogout, getCurrentUser } from '@/api/auth'
import type { LoginCredentials, AuthState } from '@/types'

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  token: string | null
  mustChangePassword: boolean
  setMustChangePassword: (v: boolean) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  })
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'))
  const [mustChangePassword, setMustChangePassword] = useState(false)

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token')
      if (token) {
        try {
          const user = await getCurrentUser()
          setMustChangePassword(user.must_change_password ?? false)
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    }

    checkAuth()
  }, [])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const tokens = await apiLogin(credentials)

    localStorage.setItem('access_token', tokens.access_token)
    localStorage.setItem('refresh_token', tokens.refresh_token)
    setToken(tokens.access_token)
    setMustChangePassword(tokens.must_change_password ?? false)

    const user = await getCurrentUser()

    setState({
      user,
      isAuthenticated: true,
      isLoading: false,
    })
  }, [])

  const logout = useCallback(async () => {
    await apiLogout()
    setToken(null)
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    })
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        token,
        mustChangePassword,
        setMustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
